import { ProductCategoriesModel } from "@dillon/Services/CommonServices";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { getProductCollectionRealTimePrices } from "@insite/client-framework/Services/ProductServiceV2";
import {
    CartLineModel,
    CartModel,
    InvoiceLineModel,
    OrderLineModel,
    ProductDto,
    ProductModel,
    QuoteLineModel,
} from "@insite/client-framework/Types/ApiModels";

type KeyPair<K extends string, V> = {
    [key in K]: V;
};

function getDataLayer() {
    return (window as any).dataLayer;
}

function setData(eventData: any) {
    const dataLayer = getDataLayer();
    if (dataLayer) {
        dataLayer.push(eventData);
    } else {
        (window as any).dataLayer = [eventData];
    }
}

async function getProductPricing(productInfo: ProductInfo) {
    const pricing = await getProductCollectionRealTimePrices({
        productPriceParameters: [
            {
                productId: productInfo.productId,
                unitOfMeasure: productInfo.unitOfMeasure,
                qtyOrdered: productInfo.qtyOrdered,
            },
        ],
    });

    return pricing.realTimePricingResults?.find(o => o.productId === productInfo.productId);
}

function getItemDetails(item: any) {
    if ("product" in item) {
        const itemTemp = item as ProductContextModel;
        return {
            product: itemTemp.productInfo != null ? itemTemp.productInfo.productId : itemTemp.product.id,
            quantity: itemTemp.productInfo != null ? itemTemp.productInfo.qtyOrdered : 0,
            price:
                itemTemp.productInfo != null
                    ? itemTemp.productInfo.pricing != null
                        ? itemTemp.productInfo.pricing.actualPrice
                        : itemTemp.product.unitListPrice
                    : itemTemp.product.unitListPrice ?? 0,
            itemName: `${itemTemp.product.productTitle} - ${itemTemp.product.productNumber}`,
            itemId: itemTemp.productInfo != null ? itemTemp.productInfo.productId : itemTemp.product.id,
            brand: itemTemp.product.brand?.name ?? "",
            variants: itemTemp.product.variantTraits?.map(vt => vt.nameDisplay).join(",") ?? "",
        };
    }

    if ("pricing" in item) {
        const itemTemp = item as CartLineModel;
        return {
            product: itemTemp.productId!,
            quantity: itemTemp.qtyOrdered ?? 0,
            price: itemTemp.pricing?.actualPrice ?? 0,
            itemName: `${itemTemp.shortDescription} - ${itemTemp.erpNumber}`,
            itemId: itemTemp.productId!,
            brand: itemTemp.brand?.name ?? "",
            variants: "",
        };
    }

    if ("invoiceLine" in item) {
        const itemTemp = item.invoiceLine as unknown as InvoiceLineModel;
        return {
            product: itemTemp.id,
            quantity: itemTemp.qtyInvoiced ?? 0,
            price: itemTemp.unitPrice ?? 0,
            itemName: `${itemTemp.productName} - ${itemTemp.productERPNumber}`,
            itemId: itemTemp.id,
            brand: itemTemp.brand?.name ?? "",
            variants: "",
        };
    }

    const itemTemp = item as OrderLineModel;
    return {
        product: itemTemp.productErpNumber,
        quantity: itemTemp.qtyOrdered!.toString(),
        price: itemTemp.unitPrice ?? "",
        itemName: `${itemTemp.shortDescription} - ${itemTemp.productErpNumber}`,
        itemId: itemTemp.productId!,
        brand: itemTemp.brand?.name ?? "",
        variants: "",
    };
}

// cartlines is either a QuoteLineModel[], a CartLineModel[] or a ProductModel (or keep adding more types)
export const setGA4Event = (cartLines: any[], eventName: string, additionalInfo?: any | null) => {
    if (IS_SERVER_SIDE) {
        return;
    }

    // Verndale customization ticket: DSSM-414
    if (eventName === "sign_in") {
        const { userID } = additionalInfo;
        const eventData = {
            event: "login",
            client_id: userID,
        } as KeyPair<string, string>;

        setData(eventData);
    }
    // End of Verndale customization ticket: DSSM-414

    // Verndale customization ticket: DSSM-407
    if (eventName === "authentication_state") {
        const { authenticationState } = additionalInfo;
        const eventData = {
            event: eventName,
            authentication_state: authenticationState,
        } as KeyPair<string, string>;

        setData(eventData);
    }
    //  End of Verndale customization ticket: DSSM-407

    // Verndale customization ticket: DSSM-430
    if (eventName === "share_list") {
        const eventData = {
            event: eventName,
        } as KeyPair<string, string>;

        setData(eventData);
    }
    // End of Verndale customization ticket: DSSM-430

    // Verndale customization ticket: DSSM-438
    if (eventName === "create_list") {
        const eventData = {
            event: eventName,
        } as KeyPair<string, string>;

        setData(eventData);
    }
    // End of Verndale customization ticket: DSSM-438

    // Verndale customization ticket: DSSM-418
    if (eventName === "file_download") {
        const eventData = {
            event: eventName || "",
            ending_url: additionalInfo.ending_url,
            click_file_extension: additionalInfo.click_file_extension,
            click_filename: additionalInfo.click_filename,
        } as KeyPair<string, string>;
        setData(eventData);
    }

    // Verndale customization ticket: DSSM-453
    if (eventName === "select_item") {
        const eventData = {
            event: eventName || "",
            ecommerce: {
                items: [],
            },
        } as KeyPair<string, any>;

        const ga4Items = cartLines;
        const itemCategories = additionalInfo?.productCategories.categories;
        const brandName = additionalInfo?.productCategories.brandName;

        ga4Items.forEach(item => {
            // Find the product categories for this item
            const itemDetails = getItemDetails(item);

            const lastCategoryName =
                itemCategories[Object.keys(itemCategories)[Object.keys(itemCategories).length - 1]];

            const itemData = {
                item_id: itemDetails.itemId,
                item_name: itemDetails.itemName,
                index: 1,
                item_brand: brandName ?? "",
                ...itemCategories,
                item_list_name: lastCategoryName ?? "",
                item_variant: "",
                price: itemDetails.price,
                quantity: itemDetails.quantity,
            } as KeyPair<string, any>;

            eventData.ecommerce.items.push(itemData);
            setData(eventData);
        });
    }
    // End of Verndale customization ticket: DSSM-453

    // Verndale customization ticket: DSSM-456
    if (eventName === "view_item") {
        const eventData = {
            event: eventName || "",
            ecommerce: {
                items: [],
            },
        } as KeyPair<string, any>;

        const ga4Items = cartLines as ProductModel[];
        const itemCategories = additionalInfo?.productCategories.categories;
        const brandName = additionalInfo?.productCategories.brandName;
        const { productsPricingPrices } = additionalInfo ?? {};

        ga4Items.forEach(async item => {
            // Find the product categories for this item
            const productInfo = createFromProduct(item);
            productInfo.pricing = (productsPricingPrices ?? []).find(
                (x: { productId: any }) => x.productId === item.id,
            );

            if (!productInfo.pricing) {
                productInfo.pricing = await getProductPricing(productInfo);
            }

            const lastCategoryName =
                itemCategories[Object.keys(itemCategories)[Object.keys(itemCategories).length - 1]];

            const itemData = {
                item_id: item.id ?? "",
                item_name: item.productTitle ? `${item.productTitle}` : "",
                index: 1,
                item_brand: brandName ?? "",
                ...itemCategories,
                item_list_name: lastCategoryName ?? "",
                item_variant: "",
                price: productInfo.pricing?.actualPrice ?? 0,
                quantity: productInfo.qtyOrdered,
            } as KeyPair<string, any>;

            eventData.ecommerce.items.push(itemData);
            setData(eventData);
        });
    }
    // End of Verndale customization ticket: DSSM-456

    // Verndale customization ticket: DSSM-469
    if (eventName === "view_cart") {
        const eventData = {
            event: eventName || "",
            ecommerce: {
                items: [],
            },
        } as KeyPair<string, any>;

        const ga4Items = cartLines as CartLineModel[];

        ga4Items.forEach(item => {
            const { productCategories } = additionalInfo;

            const categories = productCategories.find(
                (productProperty: { id: string }) => productProperty.id === item.productId,
            )?.categories;

            const categoriesIds = productCategories.find(
                (productProperty: { id: string }) => productProperty.id === item.productId,
            )?.categoriesIds;

            const brand = productCategories.find(
                (productProperty: { id: string }) => productProperty.id === item.productId,
            )?.brand;

            const itemDetails = getItemDetails(item);

            const itemData = {
                item_name: itemDetails.itemName,
                item_id: itemDetails.itemId,
                price: itemDetails.price,
                item_brand: brand ?? itemDetails.brand,
                ...categories,
                item_variant: itemDetails.variants,
                quantity: itemDetails.quantity,
            } as KeyPair<string, string | number>;

            eventData.ecommerce.items.push(itemData);
        });

        setData(eventData);
    }
    // End of Verndale customization ticket: DSSM-469

    // Verndale customization ticket: DSSM-462
    if(eventName === "remove_from_cart"){
        const eventData = {
            event: eventName || "",
            ecommerce: {
                items: [],
            },
        } as KeyPair<string, any>;

        const ga4Items = cartLines as CartLineModel[];
        
        ga4Items.forEach(item => {
            // Find the product categories for this item
            const productInfo = getItemDetails(item);  
            const { productCategories } = additionalInfo;

            const categories = productCategories.find(
                (productProperty: { id: string }) => productProperty.id === item.productId,
            )?.categories;

            const brand = productCategories.find(
                (productProperty: { id: string }) => productProperty.id === item.productId,
            )?.brand;


            const itemData = {
                item_name: productInfo.itemName,
                item_id: item.productId ?? "",
                price: productInfo.price ?? 0,
                item_brand: brand ?? productInfo.brand,
                ...categories,
                item_variant: "",
                index: 1,
                quantity: productInfo.quantity,
            } as KeyPair<string, any>;

            eventData.ecommerce.items.push(itemData);            
            setData(eventData);
        });
                  
        setData(eventData);
    }
    // End of Verndale customization ticket: DSSM-462
    
    // Verndale customization ticket: DSSM-448
    if (eventName === "view_item_list") {
        const eventData = {
            event: eventName || "",
            ecommerce: {
                items: [],
            },
        } as KeyPair<string, any>;
        

        const ga4Items = cartLines;
        ga4Items.forEach(item => {
            const productInfo = createFromProduct(item);
            const { categories, pageCategoryId, indexes, productsPricingPrices } = additionalInfo;

            const itemInfo = categories.find((category: { id: string }) => category.id === item.id);
            const brandName = itemInfo.brandName;
            const itemCategories = itemInfo.categories;

            productInfo.pricing = productsPricingPrices.find((x: { productId: any }) => x.productId === item.id);

            // pop last value from categories object
            const lastCategoryName =
                itemCategories[Object.keys(itemCategories)[Object.keys(itemCategories).length - 1]];
            const index = indexes.find((x: { id: string }) => x.id === item.id)?.attribute;
            const itemData = {
                item_name: item.productTitle ? `${item.productTitle} - ${item.productNumber}` : "",
                item_id: item.id ?? "",
                price: productInfo.pricing?.actualPrice ?? 0,
                item_brand: brandName !== null ? brandName : "",
                ...itemCategories,
                item_variant: item.variantTraits?.map((vt: { nameDisplay: any }) => vt.nameDisplay).join(",") ?? "",
                item_list_name: lastCategoryName ?? "",
                item_list_id: pageCategoryId ?? "",
                index: Number(index),
                quantity: productInfo.qtyOrdered,
            } as KeyPair<string, string | number>;

            eventData.ecommerce.items.push(itemData);
        });

        // Verndale Customization
        localStorage.removeItem("ga4Array");
        // End Verndale Customization

        setData(eventData);
    }
    // End of Verndale customization ticket: DSSM-448

    // Verndale Customization ticket: DSSM-458
    if (eventName === "add_to_cart") {

        const eventData = {
            event: eventName || "",
            ecommerce: {
                items: [],
            },
        } as KeyPair<string, any>;

        const ga4Items = cartLines;
        let indexCount = 1;
        ga4Items.forEach(item => {
            let { categories, brand, qtyOrdered, pageCategoryId } = additionalInfo;
            const qtysOrdered = additionalInfo?.qtysOrdered as CartLineModel[];
            const productCategories = additionalInfo?.productCategories as ProductCategoriesModel[];
            const itemDetails = getItemDetails(item);

            if (productCategories && productCategories.length > 0) {
                const productCategory = productCategories.find(x => x.id === itemDetails.itemId);
                const categoriesToArr = Object.values(productCategory!.categoriesIds);

                categories = productCategory?.categories;
                brand = productCategory?.brandName;
                pageCategoryId = categoriesToArr[categoriesToArr.length - 1] || "";
            }

            if (qtysOrdered) {
                qtyOrdered = qtysOrdered.find(x => x.productId === itemDetails.itemId)?.qtyOrdered;
            }
            // pop last value from categories object
            const lastCategoryName = categories[Object.keys(categories)[Object.keys(categories).length - 1]];

            const itemData = {
                item_name: itemDetails.itemName,
                item_id: itemDetails.itemId,
                price: itemDetails.price,
                item_brand: brand ?? brand,
                ...categories,
                item_variant: itemDetails.variants,
                item_list_name: lastCategoryName ?? "",
                item_list_id: pageCategoryId ?? "",
                index: indexCount,
                quantity: qtyOrdered ?? itemDetails.quantity,
            } as KeyPair<string, string | number>;
            indexCount++;

            eventData.ecommerce.items.push(itemData);
        });

        setData(eventData);
    }
    // End of Verndale Customization ticket: DSSM-458

    // Verndale Customization ticket: DSSM-480
    if (eventName === "purchase") {
        const eventData = {
            event: eventName || "",
            ecommerce: {
                currency: "",
                value: 0,
                tax: 0,
                shipping: 0,
                affiliation: "",
                transaction_id: "",
                coupon: "",
                items: [],
            },
        } as KeyPair<string, any>;

        cartLines = cartLines.filter((item: any) => item.quoteRequired !== true);
        const ga4Items = cartLines;

        ga4Items.forEach(item => {
            const { cart, billTo, orderNumber } = additionalInfo;
            let { categories, pageCategoryId, brand } = additionalInfo;
            const productCategories = additionalInfo?.productCategories as ProductCategoriesModel[];
            const itemDetails = getItemDetails(item);

            if (productCategories && productCategories.length > 0) {
                const productCategory = productCategories.find(x => x.id === itemDetails.itemId);
                const categoriesToArr = Object.values(productCategory!.categoriesIds);

                categories = productCategory?.categories;
                brand = productCategory?.brandName;
                pageCategoryId = categoriesToArr[categoriesToArr.length - 1] || "";
            }

            const itemData = {
                item_name: itemDetails.itemName,
                item_id: itemDetails.itemId,
                price: itemDetails.price,
                item_brand: brand ?? brand,
                ...categories,
                item_variant: itemDetails.variants,
                quantity: itemDetails.quantity,
            } as KeyPair<string, string | number>;

            eventData.ecommerce.currency = "USD";
            eventData.ecommerce.value = cart.orderGrandTotal;
            eventData.ecommerce.tax = cart.totalTax;
            eventData.ecommerce.shipping = cart.shippingAndHandling;
            eventData.ecommerce.affiliation = billTo.companyName ?? "";
            eventData.ecommerce.transaction_id = orderNumber;
            eventData.ecommerce.coupon = "";
            eventData.ecommerce.items.push(itemData);
        });
        
        setData(eventData);
    }

    // End of Verndale Customization ticket: DSSM-480

    // Verndale customization ticket: DSSM-470
    if(eventName === "begin_checkout"){
        const eventData = {
            event: eventName || "",
            ecommerce: {
                items: [],
            },
        } as KeyPair<string, any>;

        const ga4Items = cartLines as CartLineModel[];
        
        ga4Items.forEach(item => {
            // Find the product categories for this item
            const productInfo = getItemDetails(item);  
            const { productCategories } = additionalInfo;

            const categories = productCategories.find(
                (productProperty: { id: string }) => productProperty.id === item.productId,
            )?.categories;

            const brand = productCategories.find(
                (productProperty: { id: string }) => productProperty.id === item.productId,
            )?.brand;


            const itemData = {
                item_name: productInfo.itemName,
                item_id: item.productId ?? "",
                price: productInfo.price ?? 0,
                item_brand: brand ?? productInfo.brand,
                ...categories,
                item_variant: "",
                index: 1,
                quantity: productInfo.quantity,
            } as KeyPair<string, any>;

            eventData.ecommerce.items.push(itemData);   
        });
                  
        setData(eventData);
    }
    // End of Verndale customization ticket: DSSM-470

    // Verndale customization ticket: DSSM-474
    if(eventName === "add_shipping_info"){
        const eventData = {
            event: eventName || "",
            ecommerce: {
                items: [],
            },
        } as KeyPair<string, any>;

        const ga4Items = cartLines as CartLineModel[];
        
        ga4Items.forEach(item => {
            // Find the product categories for this item
            const productInfo = getItemDetails(item);  
            const { productCategories, shippingTier } = additionalInfo;

            const categories = productCategories.find(
                (productProperty: { id: string }) => productProperty.id === item.productId,
            )?.categories;

            const brand = productCategories.find(
                (productProperty: { id: string }) => productProperty.id === item.productId,
            )?.brand;


            const itemData = {
                shipping_tier: shippingTier ?? "",
                item_name: productInfo.itemName,
                item_id: item.productId ?? "",
                price: productInfo.price ?? 0,
                item_brand: brand ?? productInfo.brand,
                ...categories,
                item_variant: "",
                index: 1,
                quantity: productInfo.quantity,
            } as KeyPair<string, any>;

            eventData.ecommerce.items.push(itemData);       
        });
                  
        setData(eventData);
    }
    // End of Verndale customization ticket: DSSM-474

    // Verndale customization ticket: DSSM-486
    if(eventName === "add_payment_info"){
        const eventData = {
            event: eventName || "",
            ecommerce: {
                items: [],
            },
        } as KeyPair<string, any>;

        const ga4Items = cartLines as CartLineModel[];
        
        ga4Items.forEach(item => {
            // Find the product categories for this item
            const productInfo = getItemDetails(item);  
            const { productCategories, paymentType } = additionalInfo;

            const categories = productCategories.find(
                (productProperty: { id: string }) => productProperty.id === item.productId,
            )?.categories;

            const brand = productCategories.find(
                (productProperty: { id: string }) => productProperty.id === item.productId,
            )?.brand;


            const itemData = {
                paymentType: paymentType ?? "",
                item_name: productInfo.itemName,
                item_id: item.productId ?? "",
                price: productInfo.price ?? 0,
                item_brand: brand ?? productInfo.brand,
                ...categories,
                item_variant: "",
                index: 1,
                quantity: productInfo.quantity,
            } as KeyPair<string, any>;

            eventData.ecommerce.items.push(itemData);         
        });
                  
        setData(eventData);
    }
    // End of Verndale customization ticket: DSSM-486
    
// Verndale Customization ticket: DSSM-482
    if (eventName === "add_to_wishlist") {
        const eventData = {
            event: eventName || "",
            ecommerce: {
                items: [],
            },
        } as KeyPair<string, any>;

        const ga4Items = cartLines;

        ga4Items.forEach(item => {
            const itemDetails = getItemDetails(item);
            const productCategories = additionalInfo?.productCategories as ProductCategoriesModel[];
            let { categories, pageCategoryId, brand } = additionalInfo;
            if (productCategories && productCategories.length > 0) {
                const productCategory = productCategories.find(x => x.id === itemDetails.itemId);
                const categoriesToArr = Object.values(productCategory!.categoriesIds);

                categories = productCategory?.categories;
                brand = productCategory?.brandName;
                pageCategoryId = categoriesToArr[categoriesToArr.length - 1] || "";
            }

            const lastCategoryName = categories[Object.keys(categories)[Object.keys(categories).length - 1]];

            const itemData = {
                item_name: itemDetails.itemName,
                item_id: itemDetails.itemId,
                price: itemDetails.price,
                item_brand: brand ?? brand,
                ...categories,
                item_variant: itemDetails.variants,
                item_list_name: lastCategoryName ?? "",
                item_list_id: pageCategoryId ?? "",
                index: 1,
                quantity: itemDetails.quantity,
            } as KeyPair<string, string | number>;

            eventData.ecommerce.items.push(itemData);
        });

        setData(eventData);
    }
    // End of Verndale Customization ticket: DSSM-482

     // Verndale Customization Ticket DSSM-445
    if (eventName === "select_promotion") {
        const { promotionId, promotionName, creativeName, creativeSlot } = additionalInfo;
        const eventData = {
            event: eventName || "",
            ecommerce: {
                items: [{
                    promotion_id: promotionId,
                    promotion_name: promotionName,
                    creative_name: creativeName,
                    creative_slot: creativeSlot,
                }],
            },
        } as KeyPair<string, any>;
        
        setData(eventData);
    }
    // End of Verndale Customization Ticket DSSM-445
};
